import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { Sparkles, Star, TrendingUp, BookOpen, Zap, Crown, Share2, Lock, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = {
  sparkles: Sparkles,
  star: Star,
  'trending-up': TrendingUp,
  'book-open': BookOpen,
  zap: Zap,
  crown: Crown,
};

/**
 * Student/Parent-facing progress badges. Renders earned + locked cards.
 * Each earned badge has a Share button that copies the public share URL.
 *
 * Props:
 *   studentId          — required; whose badges to fetch
 *   dataTestidPrefix   — override for test ids
 *   compact            — smaller UI (used inside Parent Hub)
 */
export default function ProgressBadges({ studentId, dataTestidPrefix = 'badges', compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await api.get(`/badges/student/${studentId}`);
      setData(res.data);
    } catch { setData({ badges: [], streaks: {}, total_feedback: 0 }); }
    finally { setLoading(false); }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const share = (badge) => {
    if (!badge.share_id) return;
    const url = `${window.location.origin}/badge/${badge.share_id}`;
    navigator.clipboard.writeText(url);
    toast.success('Share link copied — paste it anywhere!');
  };

  if (loading) return (
    <div className="text-center py-10 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading badges…</div>
  );
  if (!data) return null;

  const earned = (data.badges || []).filter(b => b.earned);
  const locked = (data.badges || []).filter(b => !b.earned);

  return (
    <div data-testid={`${dataTestidPrefix}-root`}>
      {/* Header w/ streak stats */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="text-sm font-bold text-slate-900">
          <span data-testid={`${dataTestidPrefix}-earned-count`}>{earned.length}</span>
          <span className="text-slate-500 font-normal"> of {(data.badges || []).length} earned</span>
        </div>
        {data.streaks?.current_ge4 > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold">
            <TrendingUp className="w-3 h-3" /> {data.streaks.current_ge4} class streak (4+ stars)
          </span>
        )}
      </div>

      {/* Grid */}
      <div className={`grid gap-3 ${compact ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'}`}>
        {[...earned, ...locked].map(b => {
          const Icon = ICON_MAP[b.icon] || Star;
          return (
            <div
              key={b.id}
              className={`relative rounded-2xl border p-4 flex flex-col items-center text-center transition ${b.earned ? 'bg-white border-slate-200 shadow-sm hover:shadow-md' : 'bg-slate-50 border-slate-200 opacity-70'}`}
              data-testid={`${dataTestidPrefix}-card-${b.id}`}
            >
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${b.earned ? `bg-gradient-to-br ${b.gradient}` : 'bg-slate-200'}`}>
                {b.earned ? <Icon className="w-7 h-7 text-white" /> : <Lock className="w-6 h-6 text-slate-400" />}
              </div>
              <p className={`mt-2 text-xs font-extrabold ${b.earned ? 'text-slate-900' : 'text-slate-500'}`}>{b.name}</p>
              <p className="text-[10px] text-slate-500 leading-tight mt-1 min-h-[28px]">{b.description}</p>
              {b.earned && (
                <button
                  onClick={() => share(b)}
                  className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px] font-bold hover:bg-indigo-100 transition"
                  data-testid={`${dataTestidPrefix}-share-${b.id}`}
                >
                  <Share2 className="w-3 h-3" /> Share
                </button>
              )}
            </div>
          );
        })}
      </div>

      {(data.badges || []).length === 0 && (
        <div className="text-center py-8 text-sm text-slate-500 bg-white rounded-xl border border-slate-200">
          Complete your first class to unlock your first badge!
        </div>
      )}
    </div>
  );
}
